/**
 * Timesheet Deadline Settings Component
 * Allows configuration of organization-wide submission deadlines
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Clock, Calendar, Bell, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { TimesheetDeadlineSettings as DeadlineSettings } from '@/types/timesheetSettings';
import { getDayName } from '@/types/timesheetSettings';

interface TimesheetDeadlineSettingsProps {
  settings: DeadlineSettings;
  onChange?: (settings: DeadlineSettings, isValid: boolean) => void;
  onSave?: (settings: DeadlineSettings) => void;
}

export const TimesheetDeadlineSettings: React.FC<TimesheetDeadlineSettingsProps> = ({
  settings,
  onChange,
  onSave
}) => {
  const [localSettings, setLocalSettings] = useState<DeadlineSettings>(settings);

  // Notify parent of changes
  useEffect(() => {
    if (onChange) {
      // Validate settings
      const isValid = 
        localSettings.deadlineTime !== '' &&
        localSettings.gracePeriodDays >= 0 &&
        localSettings.gracePeriodDays <= 7 &&
        (!localSettings.sendReminders || localSettings.reminderDaysBefore >= 1);
      
      onChange(localSettings, isValid);
    }
  }, [localSettings, onChange]);

  // Update local state when prop changes (for reset functionality)
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleChange = (field: keyof DeadlineSettings, value: any) => {
    setLocalSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <CardTitle>Timesheet Submission Deadline</CardTitle>
        </div>
        <CardDescription>
          Configure when timesheets are due and reminder settings
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Info Alert */}
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <p className="text-sm">
              These settings apply to all employees. The deadline determines when timesheets 
              are considered late and affects dashboard notifications.
            </p>
          </AlertDescription>
        </Alert>

        {/* Deadline Day */}
        <div className="space-y-2">
          <Label htmlFor="deadline-day" className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            Deadline Day
          </Label>
          <Select
            value={localSettings.deadlineDay.toString()}
            onValueChange={(value) => handleChange('deadlineDay', parseInt(value) as 0 | 1 | 2 | 3 | 4 | 5 | 6)}
          >
            <SelectTrigger id="deadline-day">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Sunday</SelectItem>
              <SelectItem value="1">Monday</SelectItem>
              <SelectItem value="2">Tuesday</SelectItem>
              <SelectItem value="3">Wednesday</SelectItem>
              <SelectItem value="4">Thursday</SelectItem>
              <SelectItem value="5">Friday</SelectItem>
              <SelectItem value="6">Saturday</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Currently set to: <span className="font-medium">{getDayName(localSettings.deadlineDay)}</span>
          </p>
        </div>

        {/* Deadline Time */}
        <div className="space-y-2">
          <Label htmlFor="deadline-time" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Deadline Time
          </Label>
          <Input
            id="deadline-time"
            type="time"
            value={localSettings.deadlineTime}
            onChange={(e) => handleChange('deadlineTime', e.target.value)}
            className="max-w-[200px]"
          />
          <p className="text-xs text-muted-foreground">
            Time when timesheets are due (24-hour format)
          </p>
        </div>

        {/* Grace Period */}
        <div className="space-y-2">
          <Label htmlFor="grace-period">Grace Period (Days)</Label>
          <Input
            id="grace-period"
            type="number"
            min="0"
            max="7"
            value={localSettings.gracePeriodDays}
            onChange={(e) => handleChange('gracePeriodDays', parseInt(e.target.value) || 0)}
            className="max-w-[200px]"
          />
          <p className="text-xs text-muted-foreground">
            Number of days after deadline before marking as "late". 
            Submissions within this period are still considered on-time.
          </p>
        </div>

        {/* Reminders Section */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center gap-2">
            <Bell className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-semibold">Reminder Settings</h3>
          </div>

          {/* Send Reminders Toggle */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="send-reminders">Send Reminders</Label>
              <p className="text-xs text-muted-foreground">
                Notify employees before deadline
              </p>
            </div>
            <Switch
              id="send-reminders"
              checked={localSettings.sendReminders}
              onCheckedChange={(checked) => handleChange('sendReminders', checked)}
            />
          </div>

          {/* Reminder Days Before */}
          {localSettings.sendReminders && (
            <div className="space-y-2 ml-6">
              <Label htmlFor="reminder-days">Days Before Deadline</Label>
              <Input
                id="reminder-days"
                type="number"
                min="1"
                max="7"
                value={localSettings.reminderDaysBefore}
                onChange={(e) => handleChange('reminderDaysBefore', parseInt(e.target.value) || 1)}
                className="max-w-[200px]"
              />
              <p className="text-xs text-muted-foreground">
                Send first reminder this many days before the deadline
              </p>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="p-4 rounded-lg bg-muted space-y-2">
          <p className="text-sm font-medium">Current Configuration:</p>
          <ul className="text-xs text-muted-foreground space-y-1">
            <li>• Timesheets due: <span className="font-medium text-foreground">{getDayName(localSettings.deadlineDay)} at {localSettings.deadlineTime}</span></li>
            <li>• Grace period: <span className="font-medium text-foreground">{localSettings.gracePeriodDays} day{localSettings.gracePeriodDays !== 1 ? 's' : ''}</span></li>
            <li>• Reminders: <span className="font-medium text-foreground">{localSettings.sendReminders ? `Enabled (${localSettings.reminderDaysBefore} days before)` : 'Disabled'}</span></li>
          </ul>
        </div>

        {/* Legacy support: Keep individual save button if onSave is provided directly */}
        {onSave && (
          <div className="flex gap-2 pt-4 border-t">
            <Button onClick={() => onSave(localSettings)} className="flex-1">
              Save Changes
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
